import styled from 'styled-components';
import { UIData, useUIStore } from '../../../../hooks/stores/uiStore';
import { VisibilityButton } from '../../stateViewController/VisibilityButton';
import { stateViewsInfo } from '../../../../info/stateViewsInfo';
import { useScene } from '../../../../context/sceneContext';

export const DefaultViewButton = () => {
  const scene = useScene();
  const [UIData, setUIData] = useUIStore<UIData>();
  const useDirectVisibility = UIData.useDirectVisibility;

  const getVisibility = () => {
    if (!useDirectVisibility) {
      return scene.getSelectedElementsDirectVisibility([null, null]);
    }

    // In direct mode, check if all significant states are false
    const allStatesAreFalse = Object.values(stateViewsInfo).every(stateView => {
      const [stateKey, stateValue] = stateView.significantLauncherState;
      const visibility = scene.getSelectedElementsDirectVisibility([stateKey, stateValue]);
      return visibility === false;
    });

    return allStatesAreFalse ? false : true;
  };

  const visibility = getVisibility();

  return (
    <Container $visibility={visibility}>
      <SubButtonsLeftContainer className="subButtonsLeft">
        <VisibilityButton 
          forState={[null, null]}
          computedVisibility={useDirectVisibility ? visibility : undefined}
          onlyDirectVisibility={useDirectVisibility}
          noUndefined={useDirectVisibility}
        />
      </SubButtonsLeftContainer>

      <MainButton
        $isActive={UIData.currentLauncherState[0] === null}
        $computedVisibility={visibility}
        onClick={() => setUIData({ currentLauncherState: [null, null] })}
      >
        <i className="icon-anytask" style={{ fontSize: '24px' }} />
      </MainButton>
      <Label $isActive={UIData.currentLauncherState[0] === null}>Default</Label>
    </Container>
  );
};

const Container = styled.div<{ $visibility?: boolean }>`
  position: relative;
  margin-bottom: 15px;

  box-sizing: border-box;
  width: 50px;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;

  .subButtonsLeft {
    transition: opacity var(--transition-2);
    opacity: ${({ $visibility }) => ($visibility === false ? 1 : 0)};
  }
  &:hover {
    .subButtonsLeft {
      opacity: 1;
    }
  }
`;

const SubButtonsLeftContainer = styled.div`
  height: 100%;
  position: absolute;
  left: 3px;
  translate: -100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const MainButton = styled.div<{
  $isActive?: boolean;
  $isCurrentStateDifferentFromDisplayed?: boolean;
  $computedVisibility?: boolean;
}>`
  --text-color: var(--main-2);
  --text-color-hovered: var(--main-1);
  --border-color: var(--neutral-3);

  ${({ $isActive, $isCurrentStateDifferentFromDisplayed }) =>
    $isActive &&
    `
      --text-color: var(${$isCurrentStateDifferentFromDisplayed ? '--primary-2' : '--primary-1'});
      --text-color-hovered: var(--primary-1);
      --border-color: var(${$isCurrentStateDifferentFromDisplayed ? '--primary-2' : '--primary-1'});
    `}

  ${({ $computedVisibility }) =>
    $computedVisibility === false &&
    `
    --text-color: var(--error-1);
    --text-color-hovered: var(--error-1);
  `}

  cursor: pointer;
  width: 40px;
  height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;

  color: var(--text-color);
  background-color: var(--neutral-2);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-1);

  transition:
    opacity var(--transition-2),
    background-color var(--transition-2),
    color var(--transition-2),
    border var(--transition-2);
  &:hover {
    background-color: var(--neutral-3);
    color: var(--text-color-hovered);
  }
`;

const Label = styled.div<{
  $isActive: boolean;
}>`
  position: absolute;
  bottom: -25px;
  padding: 3px 7px;
  margin-bottom: 7px;

  font-family: var(--font-1);
  font-size: 12px;
  text-align: center;
  white-space: nowrap;
  text-shadow: 0px 0px 2px #000;
  color: var(--main-2);

  background-color: var(--neutral-1);
  border-radius: 5px;
  overflow: hidden;

  transition: color var(--transition-2);
  ${({ $isActive }) => $isActive && `color: var(--primary-1);`}
`;
